<template>
  <div>
    <!-- Notificazione -->
    <Notification
      :show="notification.show"
      :type="notification.type"
      :title="notification.title"
      :message="notification.message"
      @close="notification.show = false"
    />
    
    <div class="p-6 pb-0">
      <BreadCrumbs :navigation="navigation" />
      <div class="flex items-center justify-between pt-6">
        <div class="flex items-center gap-4">
          <h1 class="text-2xl font-bold text-gray-900">Visite in Attesa di Conferma</h1>
          <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
            {{ pendingVisits.data?.length || 0 }} in attesa
          </span>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-gray-600 text-sm">Aggiornamento tra: {{ remainingSeconds }} secondi</span>
          <button 
            @click="refreshData" 
            class="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-md text-sm flex items-center"
            title="Aggiorna ora"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Aggiorna ora
          </button>
        </div>
      </div>
    </div>

    <!-- Filtri -->
    <div class="p-6 pb-0">
      <div class="bg-white rounded-lg shadow p-4 mb-6">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
          <!-- Ricerca -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Ricerca</label>
            <input 
              v-model="search" 
              type="text" 
              placeholder="Nome, azienda, badge..."
              class="p93__input_general w-full px-3 py-2"
            />
          </div>
          
          <!-- Filtro referente -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Referente</label>
            <select v-model="contactFilter" class="p93__input_general w-full px-3 py-2">
              <option value="all">Tutti i referenti</option>
              <option value="none">Nessun referente</option>
              <option v-for="contact in contacts" :key="contact.id" :value="contact.id">
                {{ contact.name }}
              </option>
            </select>
          </div>
          
          <!-- Filtro data -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Data</label>
            <input 
              v-model="dateFilter" 
              type="date" 
              class="p93__input_general w-full px-3 py-2"
            />
          </div>
          
          <!-- Azioni di massa -->
          <div class="flex items-end">
            <div class="space-y-2 w-full">
              <button 
                @click="bulkConfirm" 
                :disabled="selectedVisits.length === 0"
                class="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white px-3 py-2 rounded text-sm"
              >
                Conferma Selezionate ({{ selectedVisits.length }})
              </button>
              <button 
                @click="bulkReject" 
                :disabled="selectedVisits.length === 0"
                class="w-full bg-red-500 hover:bg-red-600 disabled:bg-gray-300 text-white px-3 py-2 rounded text-sm"
              >
                Rifiuta Selezionate ({{ selectedVisits.length }})
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Lista visite in attesa -->
    <div class="p-6">
      <div v-if="pendingVisits.data && pendingVisits.data.length > 0" class="bg-white rounded-lg shadow overflow-hidden">
        <!-- Header tabella -->
        <div class="px-6 py-3 bg-gray-50 border-b border-gray-200">
          <div class="flex items-center">
            <input 
              type="checkbox" 
              :checked="allSelected"
              @change="toggleSelectAll"
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
            />
            <span class="text-sm font-medium text-gray-700">Seleziona tutto</span>
          </div>
        </div>
        
        <!-- Lista visite -->
        <div class="divide-y divide-gray-200">
          <div 
            v-for="visit in pendingVisits.data" 
            :key="visit.id" 
            class="p-6 hover:bg-gray-50 transition-colors"
            :class="{ 'bg-blue-50': selectedVisits.includes(visit.id) }"
          >
            <div class="flex items-start space-x-4">
              <!-- Checkbox -->
              <input 
                type="checkbox" 
                :value="visit.id"
                v-model="selectedVisits"
                class="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-1"
              />
              
              <!-- Avatar/Icona -->
              <div class="flex-shrink-0">
                <div class="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                  <svg class="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                  </svg>
                </div>
              </div>
              
              <!-- Informazioni visita -->
              <div class="flex-1 min-w-0">
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <!-- Colonna 1: Visitatore -->
                  <div>
                    <h3 class="text-lg font-medium text-gray-900">{{ visit.visitor_name }}</h3>
                    <p class="text-sm text-gray-600">{{ visit.visitor_company }}</p>
                    <div class="mt-2 flex items-center space-x-4">
                      <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Badge: {{ visit.badge?.code || 'N/A' }}
                      </span>
                      <span v-if="visit.badge?.visitor" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Visitatore Abituale
                      </span>
                    </div>
                  </div>
                  
                  <!-- Colonna 2: Referente e dettagli -->
                  <div>
                    <div class="space-y-2">
                      <div>
                        <span class="text-sm font-medium text-gray-700">Referente:</span>
                        <span class="text-sm text-gray-900 ml-1">
                          {{ visit.contact?.name || 'Nessun referente assegnato' }}
                        </span>
                      </div>
                      <div v-if="visit.contact?.email">
                        <span class="text-sm font-medium text-gray-700">Email:</span>
                        <span class="text-sm text-gray-900 ml-1">{{ visit.contact.email }}</span>
                      </div>
                      <div v-if="visit.contact?.phone">
                        <span class="text-sm font-medium text-gray-700">Telefono:</span>
                        <span class="text-sm text-gray-900 ml-1">{{ visit.contact.phone }}</span>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Colonna 3: Orari e tempo di attesa -->
                  <div>
                    <div class="space-y-2">
                      <div>
                        <span class="text-sm font-medium text-gray-700">Ingresso:</span>
                        <span class="text-sm text-gray-900 ml-1">{{ formatDateTime(visit.check_in_at) }}</span>
                      </div>
                      <div>
                        <span class="text-sm font-medium text-gray-700">In attesa da:</span>
                        <span class="text-sm text-gray-900 ml-1" :class="getWaitingTimeClass(visit.check_in_at)">
                          {{ getWaitingTime(visit.check_in_at) }}
                        </span>
                      </div>
                      <div v-if="visit.notified_at">
                        <span class="text-sm font-medium text-gray-700">Notificato:</span>
                        <span class="text-sm text-gray-900 ml-1">{{ formatDateTime(visit.notified_at) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Azioni -->
              <div class="flex-shrink-0 flex flex-col space-y-2">
                <button 
                  @click="confirmVisit(visit)" 
                  class="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center"
                >
                  <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  Conferma
                </button>
                <button 
                  @click="rejectVisit(visit)" 
                  class="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center"
                >
                  <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                  Rifiuta
                </button>
                <button 
                  @click="viewDetails(visit)" 
                  class="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center"
                >
                  <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  Dettagli
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Paginazione -->
        <div class="px-6 py-3 bg-gray-50 border-t border-gray-200">
          <Pagination :links="pendingVisits.meta?.links || []" />
        </div>
      </div>
      
      <!-- Messaggio quando non ci sono visite in attesa -->
      <div v-else class="bg-white rounded-lg shadow p-12 text-center">
        <svg class="w-16 h-16 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <h3 class="text-lg font-medium text-gray-900 mb-2">Nessuna visita in attesa!</h3>
        <p class="text-gray-600">Tutte le visite sono state gestite correttamente.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { Link, router } from '@inertiajs/vue3';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import Notification from '@/Components/Notification.vue';
import Pagination from '@/Components/Pagination.vue';
import { format, parseISO, differenceInMinutes, differenceInHours } from 'date-fns';
import { it } from 'date-fns/locale';

export default {
  props: ['pendingVisits', 'contacts', 'filters'],
  components: {
    Link,
    BreadCrumbs,
    Notification,
    Pagination
  },
  data() {
    return {
      search: this.filters?.search || '',
      contactFilter: this.filters?.contact_filter || 'all',
      dateFilter: this.filters?.date_filter || '',
      selectedVisits: [],
      navigation: [
        { name: 'Dashboard', href: route('dashboard') },
        { name: this.$t('modules.reception-digitale'), href: route('modules.home', 'reception-digitale') },
        { name: 'Visite in Attesa', href: route('modules.rd-pending-visits') }
      ],
      refreshInterval: 30,
      remainingSeconds: 30,
      timerId: null,
      notification: {
        show: false,
        type: 'success',
        title: '',
        message: ''
      }
    };
  },
  computed: {
    allSelected() {
      return this.pendingVisits.data?.length > 0 && 
             this.selectedVisits.length === this.pendingVisits.data.length;
    }
  },
  mounted() {
    this.startRefreshTimer();
  },
  beforeUnmount() {
    this.clearRefreshTimer();
  },
  watch: {
    search(value) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.applyFilters();
      }, 500);
    },
    contactFilter() {
      this.applyFilters();
    },
    dateFilter() {
      this.applyFilters();
    }
  },
  methods: {
    formatDateTime(dateString) {
      if (!dateString) return '';
      try {
        return format(parseISO(dateString), 'dd/MM/yyyy HH:mm', { locale: it });
      } catch (error) {
        return dateString;
      }
    },
    getWaitingTime(checkInAt) {
      if (!checkInAt) return '';
      try {
        const checkIn = parseISO(checkInAt);
        const now = new Date();
        const minutes = differenceInMinutes(now, checkIn);
        const hours = differenceInHours(now, checkIn);
        
        if (hours >= 1) {
          return `${hours}h ${minutes % 60}min`;
        }
        return `${minutes} minuti`;
      } catch (error) {
        return '';
      }
    },
    getWaitingTimeClass(checkInAt) {
      if (!checkInAt) return '';
      try {
        const checkIn = parseISO(checkInAt);
        const now = new Date();
        const minutes = differenceInMinutes(now, checkIn);
        
        if (minutes > 30) return 'text-red-600 font-semibold';
        if (minutes > 15) return 'text-yellow-600 font-medium';
        return 'text-green-600';
      } catch (error) {
        return '';
      }
    },
    startRefreshTimer() {
      this.clearRefreshTimer();
      this.remainingSeconds = this.refreshInterval;
      
      this.timerId = setInterval(() => {
        this.remainingSeconds--;
        if (this.remainingSeconds <= 0) {
          this.refreshData();
        }
      }, 1000);
    },
    clearRefreshTimer() {
      if (this.timerId) {
        clearInterval(this.timerId);
        this.timerId = null;
      }
    },
    refreshData() {
      router.reload({ 
        preserveScroll: true, 
        onSuccess: () => {
          this.startRefreshTimer();
          this.selectedVisits = []; // Reset selezioni dopo refresh
        }
      });
    },
    applyFilters() {
      const filters = {
        search: this.search,
        contact_filter: this.contactFilter,
        date_filter: this.dateFilter
      };
      
      router.get(route('modules.rd-pending-visits'), filters, { 
        preserveState: true, 
        replace: true,
        onSuccess: () => {
          this.selectedVisits = []; // Reset selezioni dopo filtro
        }
      });
    },
    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedVisits = [];
      } else {
        this.selectedVisits = this.pendingVisits.data.map(visit => visit.id);
      }
    },
    confirmVisit(visit) {
      router.post(route('modules.rd-conferma-visita', visit.id), {}, {
        preserveScroll: true,
        onSuccess: () => {
          this.showNotification('success', 'Successo', `Visita di ${visit.visitor_name} confermata`);
          this.selectedVisits = this.selectedVisits.filter(id => id !== visit.id);
        }
      });
    },
    bulkConfirm() {
      if (this.selectedVisits.length === 0) return;
      
      if (confirm(`Confermare ${this.selectedVisits.length} visite selezionate?`)) {
        router.post(route('modules.rd-bulk-conferma-visite'), {
          visit_ids: this.selectedVisits
        }, {
          preserveScroll: true,
          onSuccess: () => {
            this.showNotification('success', 'Successo', `${this.selectedVisits.length} visite confermate`);
            this.selectedVisits = [];
          }
        });
      }
    },
    bulkReject() {
      if (this.selectedVisits.length === 0) return;
      
      if (confirm(`Rifiutare ${this.selectedVisits.length} visite selezionate? I visitatori verranno fatti uscire.`)) {
        router.post(route('modules.rd-bulk-rifiuta-visite'), {
          visit_ids: this.selectedVisits
        }, {
          preserveScroll: true,
          onSuccess: () => {
            this.showNotification('success', 'Successo', `${this.selectedVisits.length} visite rifiutate`);
            this.selectedVisits = [];
          }
        });
      }
    },
    viewDetails(visit) {
      // Implementa un modal o una pagina di dettaglio
      this.showNotification('info', 'Info', `Dettagli visita di ${visit.visitor_name} - Funzionalità in sviluppo`);
    },
    showNotification(type, title, message) {
      this.notification = {
        show: true,
        type,
        title,
        message
      };
    }
  }
};
</script>

<style>
.p93__input_general {
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.p93__input_general:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}
</style>
